// shared 包内 Modal 组件的 i18n 字典
// 原因:PanelModal/InfoField/ConfirmSheet/SuccessSheet/SoldOutPopup 均位于
// shared 包,不能反向 import user-prize-list/src/render/utils/i18n.ts。
// D-24 约束:shared 包禁止 apiClient / mets 业务契约,**不禁止 UI 文案**,
// i18n 属 UI 层。sites=['US'] 仅英文。CN 仅占位防 runtime 报错。

const { useI18n } = require('@fegobull/webapi').use;
import { ELang } from '@ark/utils';

type I18nDict = Record<string, unknown>;

/**
 * METS 兑换弹窗仅配置英文文案。
 *
 * 组件可能在 CN 语言分支运行；CN 复用英文文案，避免缺失 key 直接展示给用户。
 */
const SHARED_I18N_DICT = {
  Modal: {
    // F-1/F-2/F-3
    fillTitle: 'Fill out your information',
    fillSubtitle: 'Your tickets will be sent to this email address.',
    nameLabel: 'Name',
    emailLabel: 'Email',
    namePlaceholder: 'Please enter your name as shown on ID',
    emailPlaceholder: 'Please use an active email address',
    back: 'Back',
    confirm: 'Confirm',
    // F-4
    edit: 'Edit',
    confirmTitle: "You're about to redeem?",
    confirmSubtitlePhysical:
      'This redemption cannot be reversed. Please double-check your information before confirming.',
    confirmSubtitleTicket:
      'Tickets cannot be exchanged after redemption. Please double-check the date and time before confirming.',
    // F-success
    close: 'Close',
    shareFriends: 'Share to friends',
    successTitle: 'Congratulations!',
    successTitlePhysical: "You're All Set!",
    successSubtitleTicket:
      "Your ticket redemption is confirmed.\nWe'll email the information within 3 business days",
    successSubtitleConcert:
      "Your ticket redemption is confirmed.\nWe'll email the information within 3 business days",
    successSubtitlePhysical:
      'We’ve received your info. \nOur team will reach out via email or the Moomoo app within 7 business days to finalize your prize.',
    done: 'Done',
    bragToFriends: 'Brag to Friends',
    // F-5 场次库存售罄
    soldOutTitle: 'Tickets sold out',
    soldOutSubtitle: "We'll release additional seats as soon as possible!",
    // 通用门票兑换：用户已用完本奖品全部兑换次数（无标题，仅正文）
    allRedeemedMessage: "You've claimed the tickets. See you at Citi Field!",
    ok: 'OK',
    gotIt: 'Got it',
    // F-0 场次日历(P05)
    gameSchedule: 'Game Schedule',
    gameScheduleSub: 'Select now to lock your seats',
    noEventsAvailable: 'No events available for redemption',
    // F-wish
    wishlistTitle: 'Redeem Tickets',
    wishlistHint: 'Tell us you want this, and we might make it happen!',
    wishlistAdd: 'Add to wishlist',
    wishlistAdded: 'Added',
    wishlistToast:
      "We've received your wish! If this game opens up, you'll be the first to know.",
    // F-4/F-success 行 label
    rowLabelPrize: 'Prize',
    rowLabelName: 'Name',
    rowLabelEmail: 'Email',
    rowLabelMatch: 'Match',
    rowLabelTime: 'Time',
    rowLabelVenue: 'Venue',
    rowLabelTicket: 'Ticket',
    // F-4/F-success 行 value(hardcoded 值迁入 i18n dict)
    rowValueVenueCitiField: 'New York Citi Field',
    rowValueTicketSuite: 'Suite ticket x2',
    // Loading / Submitting
    submitting: 'Submitting...',
  },
  Error: {
    invalidName: 'Please enter your full legal name (as shown on ID)',
    invalidEmail: 'Please enter a valid email format',
    ticketSoldOutDuringRedeem:
      'Tickets for this event just sold out. Please choose another event.',
    networkBusy: 'Network busy, please try again later.',
    unknown: 'Operation failed, please try again.',
  },
};

const $t = useI18n({
  [ELang.KV.CN]: SHARED_I18N_DICT,
  [ELang.KV.EN]: SHARED_I18N_DICT,
});

// NOTE: Vue2 模板里不要把本地翻译函数命名为 `$t`。
// `{{ $t(...) }}` 会被编译成 `_vm.$t(...)`，走 Vue 实例方法，
// 不是 script setup 返回的本地函数；请统一 import 默认导出为 `t` 并在模板里写 `t(...)`。

/**
 * 翻译兜底：Fego 运行态语言码异常或字典分支未命中时，useI18n 会直接返回 key。
 *
 * shared 弹窗只维护英文文案，因此 key 未命中时回查本地英文 dict，避免弹窗直接露出 i18n key。
 */
function translate(path: string): string {
  const value = $t(path);
  if (value !== path) return value;

  const fallback = path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as I18nDict)[segment];
  }, SHARED_I18N_DICT);

  return typeof fallback === 'string' ? fallback : path;
}

export default translate;
